import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, DropdownMenu, Label, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import Buttons from 'components/molecules/buttons';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import {
  PiEye,
  PiNavigationArrow,
  PiUsers,
  PiVideoCamera,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  useGetChannelListInfinite,
  usePostChannelById,
  usePostChannelByLink,
  useSyncAllChannels,
} from 'services/channel';
import {
  IChannelResponse,
  IChannelSearch,
  IChannelSyncAll,
} from 'services/channel/types/query';
import { useSearchById, useSetSearchById } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { booleanSelectOptions } from 'utils/components/inputs/select';
import { getNodes } from 'utils/table/nodes.utils';

import { MagellanPopups } from 'components/molecules';
import { Formik } from 'formik';

import { PopupActions } from 'reactjs-popup/dist/types';

import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

enum CHANNEL_COLUMNS {
  name,
  link,
  // imageLink,
}

const channelSearchInitialValues: IChannelSearch = {
  name: '',
  id: '',
  link: '',
  hasContracts: undefined,
};

const Channel = () => {
  usePermissionGuard(
    'ChannelPermissionsLIST**',
    '/',
    true,
    'music-product-digital',
  );
  const hasPermission = useHasPermission();

  const createChannelByLink = usePostChannelByLink();
  const createChannelById = usePostChannelById();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const addVisible = useComponentVisible<HTMLDivElement>(false);

  const columnsVisibility = useTableColumnsVisibility(
    'Channels1',
    CHANNEL_COLUMNS,
  );

  const i18nextPrefix = 'channel.list';

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters =
    searchById[columnsVisibility.tableId] || channelSearchInitialValues;

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, columnsVisibility.tableId);

  const { data, isLoading, fetchNextPage, isFetchingNextPage, refetch } =
    useGetChannelListInfinite(filters);

  const tableData = getNodes<IChannelResponse>(data?.pages.flat());

  const buttonStyle = { justifyContent: 'start' };

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }

    navigate(`/channel/${action.payload.id}/channel-video`);
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const hasDetailPermission = hasPermission('ChannelPermissionsLIST**', 16);
  const tableSelect = hasDetailPermission ? select : undefined;

  const syncChannel = useSyncAllChannels();

  const onSyncChannel = async (method: keyof IChannelSyncAll) => {
    await syncChannel.mutateAsync(
      {
        updateChannel: method === 'updateAll' || method === 'updateChannel',
        updateVideos: method === 'updateAll' || method === 'updateVideos',
        addNewVideos: method === 'updateAll' || method === 'addNewVideos',
      },
      {
        onSuccess: async () => {
          await refetch();
        },
      },
    );
  };

  const clearPopupValues = (setFieldValue: any) => {
    setFieldValue('channelId', undefined);
    setFieldValue('link', undefined);
  };

  const createChannelByIdFuncion = async (
    channelId: string,
    actions: PopupActions,
    setFieldValue: any,
  ) => {
    await createChannelById.mutateAsync(
      { channelId },
      {
        onSuccess: async data => {
          await refetch();
          actions.close();
          clearPopupValues(setFieldValue);
          navigate(`${data.id}/channel-video`);
        },
        onError: error => onErrorShowAllErrorsToast(error),
      },
    );
  };

  const createChannelByLinkFuncion = async (
    link: string,
    actions: PopupActions,
    setFieldValue: any,
  ) => {
    await createChannelByLink.mutateAsync(
      { link },
      {
        onSuccess: async data => {
          await refetch();
          actions.close();
          clearPopupValues(setFieldValue);
          navigate(`${data.id}/channel-video`);
        },
        onError: error => onErrorShowAllErrorsToast(error),
      },
    );
  };

  const navigateToYtChannel = (channelId: string) => {
    const targetPath = `https://www.youtube.com/channel/${channelId}`;
    window.open(targetPath, '_blank');
  };

  const formikInitialValue = {
    channelId: '',
    link: '',
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title`)}
          path=''
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={CHANNEL_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix={i18nextPrefix}
                />
              </div>
              {!!tableData.nodes.length && (
                <Buttons.ChannelSync
                  variant={BUTTON_VARIANT.default}
                  lastSyncDate={new Date(0).toISOString()}
                  onSync={onSyncChannel}
                  isPending={syncChannel.isPending}
                />
              )}
              {hasPermission('ChannelPermissionsLIST**', 1) && (
                <Button
                  variant={BUTTON_VARIANT.submit}
                  onClick={addVisible.handleComponentVisible}
                  outsideChildren={
                    <Formik
                      initialValues={formikInitialValue}
                      onSubmit={() => {}}>
                      {({
                        values,
                        handleSubmit,
                        setFieldValue,
                        ...formikHelpers
                      }) => (
                        <DropdownMenu
                          ref={addVisible.ref}
                          visible={addVisible.isComponentVisible}
                          style={{ padding: 5 }}
                          justifyPosition={'end'}>
                          <div className='flex flex-col w-full'>
                            <MagellanPopups.PopupChannelCreateForm
                              children={
                                <>
                                  <FormInput.Text
                                    type='text'
                                    label={t(`channel.popup.input.byId`)}
                                    placeholder='UCHeo8472OnB2poQyPvcBx9f'
                                    name={'channelId'}></FormInput.Text>
                                </>
                              }
                              header='channel.popup.header.byId'
                              onAdd={(actions: PopupActions) =>
                                createChannelByIdFuncion(
                                  values.channelId,
                                  actions,
                                  setFieldValue,
                                )
                              }
                              clearPopupValues={() =>
                                clearPopupValues(setFieldValue)
                              }
                              trigger={
                                <Button
                                  isClear
                                  buttonStyle={buttonStyle}
                                  fullWidth>
                                  <Button.Text
                                    text={t(`enum.TEMPLATE_TYPE_CHANNEL.byId`)}
                                  />
                                </Button>
                              }
                            />
                            <MagellanPopups.PopupChannelCreateForm
                              children={
                                <FormInput.Text
                                  type='text'
                                  label={t(`channel.popup.input.byLink`)}
                                  placeholder='https://www.youtube.com/@example'
                                  name={'link'}></FormInput.Text>
                              }
                              header='channel.popup.header.byLink'
                              onAdd={(actions: PopupActions) =>
                                createChannelByLinkFuncion(
                                  values.link,
                                  actions,
                                  setFieldValue,
                                )
                              }
                              clearPopupValues={() =>
                                clearPopupValues(setFieldValue)
                              }
                              trigger={
                                <Button
                                  isClear
                                  buttonStyle={buttonStyle}
                                  fullWidth>
                                  <Button.Text
                                    text={t(
                                      `enum.TEMPLATE_TYPE_CHANNEL.byLink`,
                                    )}
                                  />
                                </Button>
                              }
                            />
                          </div>
                        </DropdownMenu>
                      )}
                    </Formik>
                  }>
                  <Button.Text
                    text={t('button.add-what', {
                      what: t(`${i18nextPrefix}.what`),
                    })}
                  />
                </Button>
              )}
            </>
          }>
          <QueryFilter<IChannelSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IChannelSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values =>
                  setSearchById(columnsVisibility.tableId, values)
                }>
                <QueryFilter.InputsGrid>
                  <FormInput.Text name='name' label={t('input.label.name')} />
                  <FormInput.Text name='id' label={t('input.label.id')} />
                  <FormInput.Text name='link' label={t('input.label.link')} />
                  <FormInput.Select
                    name='hasContracts'
                    label={t('input.label.hasContracts')}
                    options={booleanSelectOptions}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'hasContracts'
                    ? data.value !== undefined
                      ? `${t('input.label.hasContracts')}: ${
                          data.value === true
                            ? t('input.option.yes')
                            : t('input.option.no')
                        } `
                      : undefined
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        size='compact'
        displayMode
        loading={isLoading || isFetchingNextPage}
        data={tableData}
        columns={[
          {
            label: `${t(`${i18nextPrefix}.table.name`)}`,
            renderCell: ({ name, imageLink, link }) => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <TableItem.Image url={imageLink} alt={name} />
                  <div className='flex flex-col gap-1'>
                    <TableItem.Text text={name} />
                    <TableItem.Text isSupplementary text={link} />
                  </div>
                </div>
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(CHANNEL_COLUMNS.name),
          },
          // {
          //   label: '',
          //   renderCell: ({ subscribersCount }) => (
          //     <TableItem>
          //       <Label size='compact' withText color='red'>
          //         <PiYoutubeLogo /> {subscribersCount.toLocaleString()}
          //       </Label>
          //     </TableItem>
          //   ),
          // },
          // {
          //   label: '',
          //   renderCell: ({ totalViews }) => (
          //     <TableItem>
          //       <Label size='compact' withText color='blue'>
          //         <PiEye /> {totalViews.toLocaleString()}
          //       </Label>
          //     </TableItem>
          //   ),
          // },
          // {
          //   label: '',
          //   renderCell: ({ totalVideoCount }) => (
          //     <TableItem>
          //       <Label size='compact' withText>
          //         <PiVideoCamera /> {totalVideoCount.toLocaleString()}
          //       </Label>
          //     </TableItem>
          //   ),
          // },
          {
            label: '',
            renderCell: ({
              externalChannelId,
              subscribersCount,
              totalViews,
              totalVideoCount,
            }) => (
              <TableItem justifyPosition='end'>
                <div className='flex gap-2 items-center'>
                  <Label
                    title='Subscribers'
                    size='compact'
                    withText
                    color='red'
                    style={{ minWidth: 120 }}>
                    <PiUsers />
                    {subscribersCount.toLocaleString()}
                  </Label>
                  <Label
                    size='compact'
                    title='Views'
                    withText
                    color='blue'
                    style={{ minWidth: 135 }}>
                    <PiEye /> {totalViews.toLocaleString()}
                  </Label>
                  <Label
                    title='Videos'
                    size='compact'
                    withText
                    style={{ minWidth: 100 }}>
                    <PiVideoCamera /> {totalVideoCount.toLocaleString()}
                  </Label>
                  <Button
                    type='button'
                    isClear
                    onlyIcon
                    onClick={() => navigateToYtChannel(externalChannelId)}>
                    <Button.Description description={t('button.open')} />




                    <Button.Icon
                      icon={<PiNavigationArrow transform='scale(-1,1)' />}
                    />
                  </Button>
                </div>
              </TableItem>
            ),
          },
        ]}
        onFullyScroll={fetchNextPage}
      />
    </TopbarPage>
  );
};

export default Channel;
